import { gql, useQuery, useMutation } from 'urql'
import { HOST_SERVICE } from '../../../Constants'
import { useMemo } from 'react'

const eTendersService = HOST_SERVICE + '/api/graphql'

type EnquiryByBriefIdQuery = {
  enquiryByBriefId?: {
    id: number
    enquiryAccessSecret?: string
  }
}

type EnquiryByBriefIdVariables = {
  briefId: number
}

const EnquiryByBriefIdDocument = gql`
  query EnquiryByBriefId($briefId: Int!) {
    enquiryByBriefId(briefId: $briefId) {
      id
      enquiryAccessSecret
    }
  }
`

type CreateFavouritesMutation = {
  CreateFavourites?: {
    id: number
  }[]
}

type CreateFavouritesVariables = {
  briefId: number
  spaceInterests: {
    hostId: number
    spaceId: number
  }[]
}

const CreateFavouritesDocument = gql`
  mutation CreateFavourites($briefId: Int!, $spaceInterests: [SpaceInterest!]!) {
    CreateFavourites(briefId: $briefId, spaceInterests: $spaceInterests) {
      id
    }
  }
`

type MaxRequestedVenuesQuery = {
  maxRequestedVenues: number
}

const MaxRequestedVenuesDocument = gql`
  query MaxRequestedVenues {
    maxRequestedVenues
  }
`

const useCreateFavouritesMutation = () =>
  useMutation<CreateFavouritesMutation, CreateFavouritesVariables>(CreateFavouritesDocument)

export const useCreateFavourites = (
  briefId: number | null | undefined
): {
  enquiryAccessSecret: typeof enquiryAccessSecret
  createFavourites: typeof createFavourites
  maxRequestedVenues: number | undefined
} => {
  const pause = !briefId
  const context = useMemo(
    () => ({
      url: eTendersService,
    }),
    []
  )

  const [query] = useQuery<EnquiryByBriefIdQuery, EnquiryByBriefIdVariables>({
    query: EnquiryByBriefIdDocument,
    variables: { briefId: briefId! },
    context,
    pause,
  })

  const [maxRequestedVenuesQuery] = useQuery<MaxRequestedVenuesQuery>({
    query: MaxRequestedVenuesDocument,
    context,
    pause,
  })

  const [, executeMutation] = useCreateFavouritesMutation()

  const enquiryAccessSecret = query.data?.enquiryByBriefId?.enquiryAccessSecret
  const maxRequestedVenues = maxRequestedVenuesQuery.data?.maxRequestedVenues

  // `forBriefId` covers callers whose brief was submitted after this hook ran.
  const createFavourites = (
    spaceInterests: {
      hostId: number
      spaceId: number
    }[],
    forBriefId?: number
  ) =>
    executeMutation({ briefId: forBriefId ?? briefId!, spaceInterests }, { url: eTendersService })
  return { enquiryAccessSecret, createFavourites, maxRequestedVenues }
}
